/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { Component, EventEmitter, HostListener, OnInit, Output } from '@angular/core';
import { Layer, Path, Point, Rectangle } from 'paper';
import { GraphicalTakeOffCommandService } from '../../graphical-take-off-command.service';
import { GraphicalTakeOffFormState } from '../../graphical-take-off-form.service';
import {
  newGraphicalTakeOffImageCropTransformation,
} from '../../resources';
import { fromPaperPoint } from '../canvas/canvas.utils';

/**
 * Crop component
 */
@Component({
  standalone: false,
  selector: 'gh-graphical-take-off-crop',
  templateUrl: 'graphical-take-off-crop.component.html',
  styleUrls: ['graphical-take-off-crop.component.scss'],
})
export class GraphicalTakeOffCropComponent implements OnInit {
  /**
   * Crop event emitter
   *
   * @type {EventEmitter<boolean>}
   */
  @Output() onCrop: EventEmitter<boolean> = new EventEmitter<boolean>();

  /**
   * Is component in drag mode?
   * Used for crop area drawing
   *
   * @type {boolean}
   */
  dragMode: boolean = false;

  /**
   * Crop area start point
   *
   * @type {"paper".Point}
   */
  startPoint: Point;

  /**
   * Crop rectangle end point
   *
   * @type {"paper".Point}
   */
  endPoint: Point;

  /**
   * Crop path.
   * Used for crop path displaying
   *
   * @type {"paper".Path}
   */
  cropPath: Path = new Path();

  private rasterLayer: Layer;

  constructor(private form: GraphicalTakeOffFormState,
              private commands: GraphicalTakeOffCommandService) {
    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);
  }

  /**
   * Component init.
   * Enters crop mode
   *
   * @returns {void}
   */
  ngOnInit(): void {
    this.enterCropMode();
  }

  /**
   * Cancel crop
   *
   * @returns {void}
   */
  cancel(): void {
    this.leaveCropMode();
    this.onCrop.emit(false);
  }

  /**
   * Apply crop
   *
   * @returns {void}
   */
  apply(): void {
    this.commands.currentViewImageTransform(newGraphicalTakeOffImageCropTransformation(
      fromPaperPoint(this.startPoint),
      fromPaperPoint(this.endPoint)));
    this.leaveCropMode();
    this.onCrop.emit(true);
  }

  @HostListener('window:mouseup')
  onMouseUp(): void {
    if (this.dragMode) {
      this.toggleDragMode();
      this.off();
    }
  }

  /**
   * Enter crop mode.
   * Register mouse events
   *
   * @returns {void}
   */
  private enterCropMode(): void {
    this.rasterLayer = this.commands.getRasterLayer();

    this.rasterLayer.on('mousedown', this.onMouseDown);
    this.rasterLayer.on('mousemove', this.onMouseMove);
  }

  /**
   * Leave crop mode.
   * Remove all crop path segments
   *
   * @returns {void}
   */
  private leaveCropMode(): void {
    this.off();
    this.cropPath.removeSegments();
  }

  /**
   * Toggle drag mode
   *
   * @returns {void}
   */
  private toggleDragMode(): void {
    this.dragMode = !this.dragMode;
  }

  /**
   * Unregister handlers
   *
   * @returns {void}
   */
  private off(): void {
    this.rasterLayer.off('mousedown', this.onMouseDown);
    this.rasterLayer.off('mousemove', this.onMouseMove);
  }

  private onMouseDown(event: any): void {
    this.toggleDragMode();
    this.startPoint = this.startPoint || new Point(event.point.x, event.point.y);
  }

  private onMouseMove(event: any): void {
    // Stop drawing crop area
    if (!this.dragMode) {
      return;
    }

    this.endPoint = new Point(event.point.x, event.point.y);

    // Redraw crop area on each mouse move
    this.cropPath.remove();
    this.cropPath = new Path.Rectangle(new Rectangle(this.startPoint, this.endPoint));
    this.cropPath.selected = true;
    this.cropPath.fillColor = 'rgba(0, 0, 0, .25)';
  }
}
